import { Injectable, inject, DestroyRef, signal } from '@angular/core';
import { SwUpdate, UnrecoverableStateEvent, VersionEvent} from '@angular/service-worker';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({ providedIn: 'root' })
export class UpdateService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly sw = inject(SwUpdate);

  private readonly initialized = signal(false);
  private readonly promptShown = signal(false);

  readonly updateAvailable = signal(false);

  init(): void {
    if (this.initialized()) return;
    this.initialized.set(true);

    if (!this.sw.isEnabled) return;

    this.sw.versionUpdates.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      this.handleVersionEvent(event);
    });

    this.sw.unrecoverable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      this.handleUnrecoverable(event);
    });
  }

  private handleVersionEvent(event: VersionEvent): void {
    switch (event.type) {
      case 'VERSION_DETECTED':
        this.reportInfo('sw_version_detected', {
          hash: event.version.hash,
          appData: event.version.appData,
        });
        break;

      case 'NO_NEW_VERSION_DETECTED':
        this.reportInfo('sw_no_new_version_detected');
        break;

      case 'VERSION_READY':
        this.reportInfo('sw_version_ready', {
          currentHash: event.currentVersion.hash,
          nextHash: event.latestVersion.hash,
          currentAppData: event.currentVersion.appData,
          nextAppData: event.latestVersion.appData,
        });

        this.updateAvailable.set(true);
        break;

      case 'VERSION_INSTALLATION_FAILED':
        this.reportError('sw_version_installation_failed', {
          hash: event.version.hash,
          appData: event.version.appData,
          error: this.serializeError(event.error),
        });
        break;
    }
  }

  private handleUnrecoverable(event: UnrecoverableStateEvent): void {
    this.reportError('sw_unrecoverable', {
      reason: event.reason,
    });

    window.location.reload();
  }

  async applyUpdate(): Promise<void> {
    if (!this.sw.isEnabled) return;
    await this.sw.activateUpdate();
    window.location.reload();
  }

  promptAndApplyUpdate(): void {
    if (!this.updateAvailable()) return;
    if (this.promptShown()) return;

    this.promptShown.set(true);

    const ok = window.confirm('New version available. Reload page?');
    if (ok) {
      this.reportInfo('sw_update_accepted');
      this.applyUpdate();
    } else {
      // this.reportInfo('sw_update_declined');
      this.promptShown.set(false);
    }
  }

  private reportInfo(eventName: string, data?: Record<string, unknown>): void {
    console.info(`[SW] ${eventName}`, data ?? {});
  }

  private serializeError(error: unknown): unknown {
    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    }

    return error;
  }

  private reportError(eventName: string, data?: Record<string, unknown>): void {
    console.error(`[SW] ${eventName}`, {
      ...data,
      href: window.location.href,
      userAgent: navigator.userAgent,
      online: navigator.onLine,
      time: new Date().toISOString(),
    });
  }
}
